import { UserProcessStatus_MOS } from './user-process.MOS.status';
import { UserProcessStatus_POST } from './user-process.POST.status';

/**
 * # All Available statuses for UserProcess
 * Some statuses are allowed only for specific userProcess.mode. Lot of statuses is shared between MODES.
 * Lot of statuses is allowed only with specific level of support (hasAssistantService, hasFullHandling).
 *
 * ### When talking about LEVEL / Level of support / Something like: `POST 1 / MOS 3` it means:
 * - LEVEL 1 / POST 1 / MOS 1 - userProcess.hasAssistantService === false && userProcess.hasFullHandling === false
 * - LEVEL 2 / POST 2 / MOS 2 - userProcess.hasAssistantService === true && userProcess.hasFullHandling === false
 * - LEVEL 3 / POST 3 / MOS 3 - userProcess.hasFullHandling === true (usually we don't care about hasAssistantService in this case)
 * - POST ALL - All levels of support for userProcess.mode === "POST"
 * - MOS ALL - All levels of support for userProcess.mode === "MOS"
 */
export const UserProcessStatus = {
  /**
   * ### Status set by system
   * - When user-processes (applications) purchased by the foreigner
   * - When Employee (consultant) creates new user-process (application) for the foreigner and DOES NOT set type (and category) immediately
   * ### Requirements
   * - none
   * ### What Foreigner sees:
   * - PRE-SURVEY form
   */
  FILLING_PRE_SURVEY: 'FILLING_PRE_SURVEY',

  /**
   * ### Status set by system
   * - When FOREIGNER sends his pre-survey form for verification
   * ### Requirements
   * - none
   * ### What Foreigner sees:
   * - PRE-SURVEY form with information that his form is under verification
   */
  PRE_SURVEY_VERIFICATION: 'PRE_SURVEY_VERIFICATION',

  /**
   * ### Status set by system
   * - (POST ALL | MOS 3) When the consultant reviews preSurvey form and ASSIGNS the type and category to the user-process
   * - MOS 1 & 2 DOES NOT GO THROUGH FILLING_DETAILS
   * ### Requirements
   * - `userProcess.mode === "POST"
   * - OR `userProcess.mode === "MOS" AND userProcess.hasFullHandling === true`
   * ### What Foreigner sees:
   * - (POST ALL | MOS 3) Personal Data form
   */
  FILLING_DETAILS: 'FILLING_DETAILS',

  /**
   * ### Status set by system
   * - (POST ALL | MOS 3) When the FOREIGNER sends Personal Data Form for review and it passed initial system verification (required fields, formats, etc.) - goes to documents
   * - MOS 1 & 2 DOES NOT GO THROUGH COMPLETING_DOCUMENTS
   * ### Requirements
   * - `userProcess.mode === "POST"
   * - OR `userProcess.mode === "MOS" AND userProcess.hasFullHandling === true`
   * ### What Foreigner sees:
   * - (POST ALL | MOS 3) List of documents which are required for the application - he/she is supposed to upload scans/originals of these documents for consultants to review.
   */
  COMPLETING_DOCUMENTS: 'COMPLETING_DOCUMENTS',

  /**
   * ### Status set by system
   * - (POST ALL | MOS 3) When the FOREIGNER sends all required documents for review AND his personal-data form is validated AND foreigner clicked "Send whole Application for review" (consultant starts reviewing personal-data and documents)
   * - MOS 1 & 2 DOES NOT GO THROUGH READY_FOR_REVIEW
   * ### Requirements
   * - `userProcess.mode === "POST"
   * - OR `userProcess.mode === "MOS" AND userProcess.hasFullHandling === true`
   * ### What Foreigner sees:
   * - (POST ALL | MOS 3) List of documents (With files that he uploaded). Foreigner CAN view the personal-details in read mode + CAN see the list of documents AND change the files uploaded.
   */
  READY_FOR_REVIEW: 'READY_FOR_REVIEW',

  /**
   * ### Status set by system
   * - (POST ALL | MOS 3) When the CONSULTANT (EMPLOYEE) rejects one of the documents from the list-of-documents.
   * - MOS 1 & 2 is not allowed to go through CHANGES_REQUESTED
   * ### Requirements
   * - `userProcess.mode === "POST"
   * - OR `userProcess.mode === "MOS" AND userProcess.hasFullHandling === true`
   * ### What Foreigner sees:
   * - (POST ALL | MOS 3) List of documents with notification which document is rejected and option to read the comment (reason for rejection)
   * - (POST ALL | MOS 3) Foreigner CAN now change data in personal-details form as well as all documents in list-of-documents
   */
  CHANGES_REQUESTED: 'CHANGES_REQUESTED',

  /**
   * ========================================================================================================
   * ========================================================================================================
   * ======================================== STATUSES USED ONLY WHEN userProcess.mode === 'POST'
   * ========================================================================================================
   * ========================================================================================================
   * Documentation of these statuses is in src/modules/user-processes/user-process.POST.status.ts
   */
  ...UserProcessStatus_POST,
  // ==========================================================

  /**
   * ========================================================================================================
   * ========================================================================================================
   * ======================================== STATUSES USED ONLY WHEN userProcess.mode === 'MOS'
   * ========================================================================================================
   * ========================================================================================================
   * Documentation of these statuses is in src/modules/user-processes/user-process.MOS.status.ts
   */
  ...UserProcessStatus_MOS,
  // ==========================================================

  /**
   * ### Status set by system
   * - (POST 2) AFTER the consultant confirms: "I have reviewed the personal-data and documents" AND AFTER documents(main application, statements, checklist) have been generated succesfully.
   * - (MOS 2) AFTER appointment (consultation) for application verification -> consultant confirms: "Appointment completed, documents verified"
   * - (MOS 3) AFTER appointment to submit application in MOS -> consultant confirms: "Appointment completed, application submitted to MOS"
   * ### Requirements
   * - `userProcess.mode === "POST" AND userProcess.hasAssistantService === true && userProcess.hasFullHandling === false`
   * - OR `userProcess.mode === "MOS" AND userProcess.hasAssistantService === true && userProcess.hasFullHandling === false`
   * - OR `userProcess.mode === "MOS" AND userProcess.hasAssistantService === true && userProcess.hasFullHandling === true`
   * ### What Foreigner sees:
   *  - (POST 2) Instructions & address for submitting the documents by post to the voivo-office + final-review docs upload option
   *  - (POST 2) Printing Main request form + checklist
   *  - (POST 2) (MOS 2+3) Instructions with information that when he receives the date of visit to the voivo-office he needs to insert it here in the app
   *  - (POST 2) (MOS 2+3) Instructions that if he received correspondence from voivo-office, he can upload it here so that we can help with it
   *  - (POST 2) (MOS 2+3) Option to print checklist with information that it will be needed later
   *  - (POST 2) (MOS 2+3) Option to upload documents for final-review
   */
  AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER: 'AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER',

  /**
   * ### Status set by system
   * - (MOS 2+3 | POST 2) AFTER the FOREIGNER provided the date of visit to the voivo-office
   * ### Requirements
   * - `userProcess.mode === "POST" AND userProcess.hasAssistantService === true && userProcess.hasFullHandling === false`
   * - OR `userProcess.mode === "MOS" AND userProcess.hasAssistantService === true && userProcess.hasFullHandling === false`
   * - OR `userProcess.mode === "MOS" AND userProcess.hasFullHandling === true`
   * ### What Foreigner sees:
   *  - Date of the visit & address of the immigration office where the visit is taking place.
   *  - Option to Change the date of the visit (if he needs to reschedule it) - this will notify the consultant and he will help with rescheduling
   *  - Instructions to prepare documents from the LIST OF REQUIRED DOCUMENTS (checklist) + POWER OF ATTORNEY (If needed)
   *  - Option to upload documents for final-review
   * -  Printing Main Request Form + Statements (as optional because these are not needed now)
   */
  IMMIGROF_VISIT_INFO_PROVIDED: 'IMMIGROF_VISIT_INFO_PROVIDED',

  /**
   * ### Status set by system
   * - (POST 2+3 | MOS 2+3) AFTER the CONSULTANT (EMPLOYEE) confirmed that the visit in the voivo office already took place
   * ### Requirements
   * - `userProcess.mode === "POST" AND userProcess.hasAssistantService === true && userProcess.hasFullHandling === false`
   * - OR `userProcess.mode === "POST" AND userProcess.hasFullHandling === true`
   * - OR `userProcess.mode === "MOS" AND userProcess.hasAssistantService === true && userProcess.hasFullHandling === false`
   * - OR `userProcess.mode === "MOS" AND userProcess.hasFullHandling === true`
   * ### What Foreigner sees:
   * - (POST 2+3 | MOS 2+3) All see same stuff:
   *  - Information that the visit already took place, thank you for your cooperation
   *  - When he receives the decision - upload it here so that we can help with it - date of issue / valid until / decision document scan
   *  - Information that if we receive decision in his name - we will upload it here and notify him about it immediately
   */
  IMMIGROF_VISIT_COMPLETED: 'IMMIGROF_VISIT_COMPLETED',

  /**
   * ### Status set by system
   * - (POST 2+3 | MOS 2+3) AFTER the CONSULTANT (EMPLOYEE) uploaded the decision document (scan) received from the voivo-office with information about the decision (date of issue, valid until, etc.)
   * - OR (POST 2+3 | MOS 2+3) AFTER the FOREIGNER uploaded the decision document (scan) received from the voivo-office with information about the decision (date of issue, valid until, etc.)
   * ### Requirements
   * - `userProcess.mode === "POST" AND userProcess.hasAssistantService === true && userProcess.hasFullHandling === false`
   * - OR `userProcess.mode === "POST" AND userProcess.hasFullHandling === true`
   * - OR `userProcess.mode === "MOS" AND userProcess.hasAssistantService === true && userProcess.hasFullHandling === false`
   * - OR `userProcess.mode === "MOS" AND userProcess.hasFullHandling === true`
   * ### What Foreigner sees:
   * - (POST 2+3 | MOS 2+3) All see same stuff:
   *  - Information that the visit already took place, thank you for your cooperation
   *  - When he receives the decision - upload it here so that we can help with it - date of issue / valid until / decision document scan
   *  - Information that if we receive decision in his name - we will upload it here and notify him about it immediately
   */
  DECISION_ISSUED: 'DECISION_ISSUED',

  /**
   * ### Status set by system
   * - (POST ALL | MOS ALL) AFTER consultant (EMPLOYEE) marks the user-process as archived.
   * ### Requirements
   * - none
   * ### What Foreigner sees:
   * - FOREIGNER SHOULD NOT BE ABLE TO DISPLAY USER-PROCESS WITH STATUS === ARCHIVED.
   *  - Not on any sort of list
   *  - Not any details of it
   */
  ARCHIVED: 'ARCHIVED',
};
export type UserProcessStatusType = (typeof UserProcessStatus)[keyof typeof UserProcessStatus];

/**
 * The codes below ARE ONLY INFORMATIONAL
 * FOR INTERNAL PURPOSES
 * THESE ARE NOT USED ANYWHERE AND should not be used in any logic
 * These can help to navigate through `user-process/summary` directories and understand which status is used in which component
 *
 * [USER-PROCESS-STATUS]: [CODE]
 * FILLING_PRE_SURVEY       1
 * PRE_SURVEY_VERIFICATION  2
 * FILLING_DETAILS          3
 * COMPLETING_DOCUMENTS     4
 * READY_FOR_REVIEW         5
 * CHANGES_REQUESTED        6
 *
 * -- ONLY POST:
 * REVIEW_DONE_POST_SUBMISSION_INSTRUCTIONS     7P-A              - review-done-submission-instructions - only L1
 * SELECTING_DOCUMENTS_SIGN_MODE                7P-C-0 + 7P-C-1   - L3 only, selecting + selected
 * DOCUMENTS_SENT_TO_OFFICE_BY_POST             7P-C-2            - L3 only, documents sent to the company office
 * SIGNED_DOCUMENTS_RECEIVED                    7P-C-3            - L3 only, signed documents received in the companny office
 * APPOINTMENT_TO_SIGN_DOCS                     8P-C-0            - L3 only, appointment to sign docs in the company office
 * AWAITING_IMMIGROF_VISIT_INFO__FROM_IMMIGROF  9P-C              - L3 only, waiting for the date of visit to the voivo-office
 * IMMIGROF_VISIT_SCHEDULED                     10P-C             - L3 only, date of visit to the voivo-office is set
 *
 *
 * -- MIXED/MOS:
 * APPOINTMENT_TO_VERIFY_DOCS:
 *  - L1+L2: 8M-AB-0    mos-draft - User is asked to prepare draft in mos and after confirmation sets up appointment
 *  - L1+L2: 8M-AB-1    mab-appointment-setup - User is asked to request appointment date for documents verification by the sepcialist
 *  - L1+L2: 8M-AB-2    appointment-documents-verify - User waits for the appointment where he will verify the docs in mos with specialist
 *
 * APPOINTMENT_TO_SUBMIT_TO_MOS:
 *  - L3:    8M-C-1     mc-appointment-setup - User is asked to request appointment for submitting the application in mos by the specialist
 *  - L3:    8M-C-2     appointment-mos-submission - User waits for appointment to submit IN MOS BY THE SPECIALIST
 *
 * REVIEW_DONE_MOS_SUBMISSION_INSTRUCTIONS:
 *  - L1:    9M-A       review-done-mos-submission-instructions - review done support for this user ends at this stage.
 *
 * AWAITING_IMMIGROF_VISIT_INFO__FROM_FOREIGNER:
 *  - L2(MOS):     9M-B       mb-providing-immigrof-visit-date - review done now foreigner has to provide us with the immigrof-visit date
 *  - L2(POST):    9P-B-1     post-submission-instructions - foreigner sees instructions how to send his application by post to the immigrof and he has to confirm that he did this
 *  - L2(POST):    9P-B-2     pb-providing-immigrof-visit-date - foreigner sent the application by post to the immmigrof and he has to provide us with the immigrof-visit date
 *  - L3(MOS):     9M-C       mc-providing-immigrof-visit-date - specialist sent the application in MOS and now foreigner has to provide us with the immigrof-visit date
 *
 * IMMIGROF_VISIT_INFO_PROVIDED:
 *  - L2(MOS):     10M-B      mb-immigrof-visit-info-provided - foreigner provided the date of visit to the voivo-office
 *  - L3(MOS):     10M-C      mc-immigrof-visit-info-provided - foreigner provided the date of visit to the voivo-office
 *  - L2(POST):    10P-B      pb-immigrof-visit-info-provided - foreigner provided the date of visit to the voivo-office
 *
 * IMMIGROF_VISIT_COMPLETED:
 *  - L2(MOS):     11M-B      mb-immigrof-visit-completed - immgrof-visit completed. support ends here. upload decision if you want further support.
 *  - L2(POST):    11P-B      pb-immigrof-visit-completed - immgrof-visit completed. support ends here. upload decision if you want further support.
 *  - L3(MOS):     11M-C      mc-immigrof-visit-completed - immgrof-visit completed. upload decision when you receive it.
 *  - L3(POST):    11P-C      pc-immigrof-visit-completed - immgrof-visit completed. upload decision when you receive it.
 *
 *  - DECISION_ISSUED: 12
 *  - ARCHIVED: 13
 */
